import * as React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { claimGiftVoucher } from 'Services/Api/_HumanWritten/GiftCertificateService';
import { GiftVoucherDto } from 'Views/Components/_HumanWritten/AccountDetails/VoucherSection/GiftVouchersUtils';
import { VoucherTicket } from 'Views/Components/_HumanWritten/PurchaseGiftCertificate/VoucherTicket';
import { formatStandardDate } from 'Util/_HumanWritten/DateUtils';
import { LottieSpinner } from '../Components/_HumanWritten/Lottie/LottieSpinner';
import NavigationWrapper from '../Components/Navigation/NavigationWrapper';
import { Button, Colors, Display } from '../Components/Button/Button';
import { Flex } from 'Views/Components/Flex/Flex';
import Card from 'Views/Components/_HumanWritten/Card/Card';
import { themeStore } from 'Models/ThemeStore';

type ClaimStatus = 'loading' | 'success' | 'already-yours' | 'already-claimed' | 'error';

export default function ClaimVoucherPage() {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const code = searchParams.get('code') ?? '';

	const [status, setStatus] = useState<ClaimStatus>('loading');
	const [errorMessage, setErrorMessage] = useState('');
	const [claimedVoucher, setClaimedVoucher] = useState<GiftVoucherDto | null>(null);

	useEffect(() => {
		document.title = 'Claim Voucher';

		if (!code) {
			setErrorMessage('No voucher code provided.');
			setStatus('error');
			return;
		}

		claimGiftVoucher(code)
			.then(response => {
				setClaimedVoucher(response.data);
				setStatus('success');
			})
			.catch(error => {
				const httpStatus = error.response?.status;
				const message = error.response?.data;
				if (httpStatus === 409) {
					setStatus('already-yours');
				} else if (httpStatus === 410) {
					setStatus('already-claimed');
				} else {
					setErrorMessage(
						typeof message === 'string'
							? message
							: 'Something went wrong. Please try again or contact support.',
					);
					setStatus('error');
				}
			});
	}, [code]);

	useEffect(() => {
		if (status !== 'success' || !claimedVoucher) {
			return undefined;
		}
		const timer = setTimeout(() => {
			navigate(`/account-details/credits?claimed=${claimedVoucher.id}`, { replace: true });
		}, 4000);
		return () => clearTimeout(timer);
	}, [status, claimedVoucher, navigate]);

	if (status === 'loading') {
		return <LottieSpinner />;
	}

	// Success state - show the voucher
	if (status === 'success' && claimedVoucher) {
		return (
			<div className="body-content">
				<NavigationWrapper className="booking-success__nav" />
				<div className="claim-voucher-page">
					<div className="claim-voucher-page__icon">
						<svg className="animated-checkmark" viewBox="0 0 52 52">
							<circle className="animated-checkmark__circle" cx="26" cy="26" r="24" fill="none" />
							<path className="animated-checkmark__check" fill="none" d="M15 27l7 7 15-15" />
						</svg>
					</div>
					<h3>Voucher claimed!</h3>
					<p className="claim-voucher-page__subtitle">This voucher has been added to your account.</p>
					<div className="claim-voucher-page__ticket">
						<VoucherTicket
							isGift
							code={claimedVoucher.code}
							amount={claimedVoucher.remainingValue}
							initialAmount={claimedVoucher.initialValue}
							status="active"
							expiryText={`Expires ${formatStandardDate(claimedVoucher.expiryDate)}`}
							hasMessage={!!claimedVoucher.message}
							message={claimedVoucher.message}
							showMessage={!!claimedVoucher.message}
						/>
					</div>
					<p className="claim-voucher-page__redirect">Redirecting to your credits...</p>
				</div>
			</div>
		);
	}

	// Error states
	let title = 'Unable to claim voucher';
	let details = errorMessage;

	if (status === 'already-yours') {
		title = 'Already claimed';
		details = "You have already claimed this voucher. It's in your credits.";
	} else if (status === 'already-claimed') {
		title = 'Already claimed';
		details =
			'This voucher has already been added to another account. If you believe this is a mistake, please contact our staff for assistance.';
	}

	return (
		<>
			<NavigationWrapper />
			<div className="body-content error-page">
				<div
					className="error-page__background"
					style={{
						backgroundImage: `url(${themeStore.errorPageBackgroundImageURL})`,
						backgroundSize: 'cover',
						backgroundPosition: 'center',
					}}
				/>
				<div className="error-page__content">
					<Card>
						<h5>{title}</h5>
						<p className="body--content">{details}</p>
						<Flex direction="col" gap="xs">
							{status === 'already-yours' && (
								<Button
									display={Display.Solid}
									colors={Colors.Primary}
									newStyles
									onClick={() =>
										navigate(`/account-details/credits?search=${encodeURIComponent(code)}`)
									}
								>
									View voucher
								</Button>
							)}
							<Button
								display={status === 'already-yours' ? Display.Outline : Display.Solid}
								colors={status === 'already-yours' ? Colors.Secondary : Colors.Primary}
								newStyles
								onClick={() => navigate('/home')}
							>
								Go to home page
							</Button>
						</Flex>
					</Card>
				</div>
			</div>
		</>
	);
}
